<template>
  <div v-if="promotion" class="portal-promotion-detail-page">
    <Menu class="portal-shell-menu" />

    <div class="portal-promotion-detail-page__container">
      <article class="portal-promotion-detail-page__panel">
        <div class="portal-promotion-detail-page__media">
          <span
            v-if="statusLabel"
            class="portal-promotion-detail-page__status-badge"
            :class="`portal-promotion-detail-page__status-badge--${promotion.status}`"
          >
            {{ statusLabel }}
          </span>

          <div
            class="promo-card__image portal-promotion-detail-page__visual"
            :class="
              showPromoImage
                ? 'promo-card__image--asset'
                : `promo-card__image--${promotion.type}`
            "
          >
            <img
              v-if="showPromoImage"
              class="promo-card__img"
              :src="promoImageSrc"
              alt=""
              @error="imageFailed = true"
            />
            <div v-else class="promo-card__image-object"></div>
          </div>
        </div>

        <div class="portal-promotion-detail-page__header-card">
          <div class="portal-promotion-detail-page__header-main">
            <h1 class="portal-promotion-detail-page__title">{{ promotion.title }}</h1>
            <p class="portal-promotion-detail-page__period">{{ periodLabel }}</p>
          </div>

          <div class="portal-promotion-detail-page__header-aside">
            <div v-if="pointsLabel" class="portal-promotion-detail-page__reward">
              <span class="portal-promotion-detail-page__reward-label">
                {{ $t("portalHome.promotions.detailRewardLabel") }}
              </span>
              <span class="portal-promotion-detail-page__reward-value">{{ pointsLabel }}</span>
            </div>

            <div class="portal-promotion-detail-page__header-actions">
              <button
                v-if="promotion.requiresSignIn && !promotion.signedIn"
                type="button"
                class="portal-promotion-detail-page__cta"
                :disabled="signingInPromotionId === promotion.id"
                @click="handlePromotionSignIn(promotion.id)"
              >
                {{ $t("portalHome.promotions.signInButton") }}
              </button>
              <button
                v-else-if="promotion.requiresSignIn && promotion.signedIn"
                type="button"
                class="portal-promotion-detail-page__cta portal-promotion-detail-page__cta--muted"
                disabled
              >
                {{ $t("portalHome.promotions.signedInButton") }}
              </button>
              <span v-else class="portal-promotion-detail-page__cta-hint">
                {{ $t("portalHome.promotions.detailAutoApply") }}
              </span>
            </div>
          </div>
        </div>
      </article>

      <div class="portal-promotion-detail-page__layout">
        <section class="portal-promotion-detail-page__main">
          <div class="portal-promotion-detail-page__content-block">
            <h2>{{ $t("portalHome.promotions.detailDescriptionTitle") }}</h2>
            <div
              v-if="descriptionHtml"
              class="portal-promotion-detail-page__rich-text"
              v-html="descriptionHtml"
            />
            <p v-else class="portal-promotion-detail-page__empty-copy">
              {{ $t("portalHome.promotions.detailNoDescription") }}
            </p>
          </div>

          <div
            v-if="promotion.rules?.length"
            class="portal-promotion-detail-page__content-block portal-promotion-detail-page__content-block--rules"
          >
            <h2>{{ $t("portalHome.promotions.detailRulesTitle") }}</h2>
            <ul>
              <li v-for="(rule, index) in promotion.rules" :key="index">{{ rule }}</li>
            </ul>
          </div>
        </section>

        <aside class="portal-promotion-detail-page__sidebar">
          <section class="portal-promotion-detail-page__side-card">
            <h2>{{ $t("portalHome.promotions.detailInfoTitle") }}</h2>
            <dl class="portal-promotion-detail-page__info-list">
              <div
                v-for="(row, index) in infoRows"
                :key="`${row.label}-${index}`"
                class="portal-promotion-detail-page__info-row"
              >
                <dt>{{ row.label }}</dt>
                <dd
                  :class="{
                    'portal-promotion-detail-page__info-value--status-active':
                      row.tone === 'active',
                    'portal-promotion-detail-page__info-value--accent': row.tone === 'accent',
                  }"
                >
                  {{ row.value }}
                </dd>
              </div>
            </dl>
          </section>

          <section class="portal-promotion-detail-page__side-card portal-promotion-detail-page__side-card--help">
            <h2>{{ $t("portalHome.promotions.detailHelpTitle") }}</h2>
            <p>{{ $t("portalHome.promotions.detailHelpText") }}</p>
            <router-link
              class="portal-promotion-detail-page__help-link"
              :to="{ name: 'contact_service' }"
            >
              {{ $t("portalHome.promotions.detailHelpContact") }}
            </router-link>
          </section>
        </aside>
      </div>

      <section v-if="relatedPromotions.length" class="portal-promotion-detail-page__related">
        <div class="portal-promotion-detail-page__related-head">
          <h2>{{ $t("portalHome.promotions.detailRelatedTitle") }}</h2>
          <router-link
            class="portal-promotion-detail-page__related-link"
            :to="{ name: 'dashboard', hash: '#portal-promotions' }"
          >
            {{ $t("portalHome.promotions.viewAll") }}
          </router-link>
        </div>
        <div class="portal-promotion-detail-page__related-grid">
          <PromotionCard
            v-for="item in relatedPromotions"
            :key="item.id"
            :promotion="item"
            :signing-in="signingInPromotionId === item.id"
            @sign-in="handlePromotionSignIn"
          />
        </div>
      </section>
    </div>

    <PortalFooter
      :links="portalFooterLinks"
      :socials="portalFooterSocials"
      :logo-src="portalFooterLogoSrc"
    />
  </div>

  <div
    v-else-if="promotionsLoading"
    class="portal-promotion-detail-page portal-promotion-detail-page--loading"
  >
    <Menu class="portal-shell-menu" />
    <div class="portal-promotion-detail-page__container">
      <p>{{ $t("portalHome.promotions.loading") }}</p>
    </div>
  </div>

  <div v-else class="portal-promotion-detail-page portal-promotion-detail-page--missing">
    <Menu class="portal-shell-menu" />
    <div class="portal-promotion-detail-page__container">
      <p>{{ $t("portalHome.promotions.detailNotFound") }}</p>
      <router-link
        class="portal-promotion-detail-page__back"
        :to="{ name: 'dashboard', hash: '#portal-promotions' }"
      >
        {{ $t("portalHome.promotions.backToList") }}
      </router-link>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, ref, watch, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import Menu from "@/components/Layout/Menu.vue";
import PromotionCard from "@/components/PortalHome/PromotionCard.vue";
import PortalFooter from "@/components/PortalHome/PortalFooter.vue";
import { usePortalPromotions } from "@/composables/usePortalPromotions";
import { resolveBundledAssetUrl } from "@/utils/resolveBundledAssetUrl";
import {
  portalFooterLinks,
  portalFooterSocials,
  portalFooterLogoSrc,
} from "@/data/portal_homepage.js";

const props = defineProps({
  slug: { type: String, required: true },
});

const { t, locale } = useI18n();

const {
  promotions,
  promotionsLoading,
  signingInPromotionId,
  findPromotionBySlug,
  ensurePromotionForSlug,
  handlePromotionSignIn,
} = usePortalPromotions({ loadOnMount: true });

const promotion = ref(null);
let promotionResolveGeneration = 0;

watchEffect(async () => {
  const slug = props.slug;
  const generation = ++promotionResolveGeneration;

  if (promotionsLoading.value) {
    return;
  }

  const fromList = findPromotionBySlug(slug);
  if (fromList) {
    if (generation === promotionResolveGeneration) {
      promotion.value = fromList;
    }
    return;
  }

  const resolved = await ensurePromotionForSlug(slug);
  if (generation === promotionResolveGeneration) {
    promotion.value = resolved;
  }
});

const relatedPromotions = computed(() =>
  promotions.value
    .filter((item) => item.slug !== props.slug)
    .slice(0, 3),
);

const promoImageSrc = computed(() =>
  resolveBundledAssetUrl(promotion.value?.imageSrc),
);
const imageFailed = ref(false);
const showPromoImage = computed(() => promoImageSrc.value && !imageFailed.value);

watch(promoImageSrc, () => {
  imageFailed.value = false;
});

const formatDate = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(locale.value, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const periodLabel = computed(() => {
  if (!promotion.value) return "";
  const start = formatDate(promotion.value.promotionStart);
  const end = formatDate(promotion.value.promotionEnd);
  return t("portalHome.promotions.detailPeriod", { start, end });
});

const pointsLabel = computed(() => {
  if (!promotion.value) return "";
  const value = promotion.value.promotionValue;
  if (promotion.value.amountType === "multiply" && value != null) {
    return t("portalHome.promotions.pointsLabel.multiply", { value });
  }
  if (promotion.value.amountType === "add" && value != null) {
    return t("portalHome.promotions.pointsLabel.add", { value });
  }
  return "";
});

const statusLabel = computed(() => {
  if (!promotion.value?.status) return "";
  const key = `portalHome.promotions.status.${promotion.value.status}`;
  const translated = t(key);
  return translated === key ? "" : translated;
});

const highlightLabel = (item) =>
  item.labelKey ? t(item.labelKey) : item.label || "";

const highlightValue = (item) =>
  item.valueKey ? t(item.valueKey) : item.value || "";

const infoRows = computed(() => {
  if (!promotion.value) {
    return [];
  }

  const rows = [];

  if (statusLabel.value) {
    rows.push({
      label: t("portalHome.promotions.detailInfoStatus"),
      value: statusLabel.value,
      tone: promotion.value.status === "active" ? "active" : null,
    });
  }

  const start = formatDate(promotion.value.promotionStart);
  if (start) {
    rows.push({
      label: t("portalHome.promotions.detailInfoStart"),
      value: start,
    });
  }

  const end = formatDate(promotion.value.promotionEnd);
  if (end) {
    rows.push({
      label: t("portalHome.promotions.detailInfoEnd"),
      value: end,
    });
  }

  if (pointsLabel.value) {
    rows.push({
      label: t("portalHome.promotions.detailInfoBonus"),
      value: pointsLabel.value,
      tone: "accent",
    });
  }

  (promotion.value.highlights || []).forEach((item) => {
    const label = highlightLabel(item);
    const value = highlightValue(item);
    if (label && value) {
      rows.push({ label, value });
    }
  });

  return rows;
});

const descriptionHtml = computed(() => {
  if (!promotion.value) {
    return "";
  }

  return (
    promotion.value.longDescription?.trim() ||
    promotion.value.description?.trim() ||
    ""
  );
});
</script>

<style lang="scss">
@import "@/styles/portal-home/portal-shell-menu-gap";
@import "@/styles/portal-home/bonus-progress-widget-shared";
</style>

<style scoped lang="scss">
@import "@/styles/portal-home/portal-promotion-detail-page";
@import "@/styles/portal-home/promotion-card";
</style>
